/* ────────────────────────────────────────────────────────────────────
   SU Predictive Search v2
   - Queries products + collections + queries + articles + pages
   - Shows Recent (localStorage) + Trending (data-trending attr) when empty
   - Bigger limits + better visual hierarchy
   - Recent search list capped at 5, deduplicated, persisted in localStorage
     key 'su_recent_searches'.
   ──────────────────────────────────────────────────────────────────── */

const SU_RECENT_KEY = 'su_recent_searches';
const SU_RECENT_MAX = 5;

function suGetRecent() {
  try { return JSON.parse(localStorage.getItem(SU_RECENT_KEY) || '[]'); }
  catch (e) { return []; }
}
function suSaveRecent(query) {
  if (!query || !query.trim()) return;
  var q = query.trim();
  var list = suGetRecent().filter(function (x) { return x.toLowerCase() !== q.toLowerCase(); });
  list.unshift(q);
  list = list.slice(0, SU_RECENT_MAX);
  try { localStorage.setItem(SU_RECENT_KEY, JSON.stringify(list)); } catch (e) {}
}
function suClearRecent() {
  try { localStorage.removeItem(SU_RECENT_KEY); } catch (e) {}
}
function suEscapeHtml(s) {
  return String(s).replace(/[&<>"']/g, function (c) {
    return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
  });
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;
    this.trending = (this.dataset.trending || '').split('|').map(function (s) { return s.trim(); }).filter(Boolean);
    this.searchUrl = (window.routes && window.routes.search_url) || '/search';

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 250).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    /* Delegated clicks for recent/trending chips + clear-recent button */
    this.addEventListener('click', (e) => {
      var chip = e.target.closest('[data-su-search-term]');
      if (chip) {
        e.preventDefault();
        var term = chip.getAttribute('data-su-search-term');
        this.input.value = term;
        this.input.focus();
        this.getSearchResults(term);
        return;
      }
      if (e.target.closest('[data-su-clear-recent]')) {
        e.preventDefault();
        suClearRecent();
        this.renderEmptyState();
      }
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) {
      this.renderEmptyState();
      return;
    }
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    var q = this.getQuery();
    if (this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }
    if (q) suSaveRecent(q);
    if (!q.length) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) {
      this.renderEmptyState();
      return;
    }
    if (this.getAttribute('results') === 'true') this.open();
    else this.getSearchResults(searchTerm);
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    event.preventDefault();
    switch (event.code) {
      case 'ArrowUp':   this.switchOption('up'); break;
      case 'ArrowDown': this.switchOption('down'); break;
      case 'Enter':     this.selectOption(); break;
      case 'Escape':    this.close(true); break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li[role="option"]');
    if (!allElements.length) return;
    let activeElement = allElements[0];
    if (moveUp && !selectedElement) return;
    this.statusElement && (this.statusElement.textContent = '');
    if (!moveUp && selectedElement) activeElement = selectedElement.nextElementSibling || allElements[0];
    else if (moveUp) activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    if (activeElement === selectedElement) return;
    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
    if (selectedProduct) selectedProduct.click();
  }

  /* ── Empty state: render Recent + Trending ───────────────────────── */
  renderEmptyState() {
    var recent = suGetRecent();
    var trending = this.trending;
    if (!recent.length && !trending.length) {
      this.close(true);
      return;
    }
    var html = '<div class="su-search-empty">';
    if (recent.length) {
      html += '<div class="su-search-section">';
      html += '<div class="su-search-section__head">';
      html += '<h3 class="su-search-section__title">Recent searches</h3>';
      html += '<button type="button" class="su-search-section__clear" data-su-clear-recent>Clear</button>';
      html += '</div>';
      html += '<ul class="su-search-chips" role="listbox" aria-label="Recent searches">';
      recent.forEach(function (term) {
        html += '<li role="option"><a href="' + this.searchUrl + '?q=' + encodeURIComponent(term) + '" class="su-search-chip" data-su-search-term="' + suEscapeHtml(term) + '">' +
                '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 12a9 9 0 0 1 9-9c2.5 0 4.8.9 6.5 2.5L21 3"/><path d="M21 3v6h-6"/><path d="M12 7v5l3 2"/></svg>' +
                '<span>' + suEscapeHtml(term) + '</span></a></li>';
      }.bind(this));
      html += '</ul></div>';
    }
    if (trending.length) {
      html += '<div class="su-search-section">';
      html += '<div class="su-search-section__head">';
      html += '<h3 class="su-search-section__title">Trending</h3>';
      html += '</div>';
      html += '<ul class="su-search-chips" role="listbox" aria-label="Trending searches">';
      trending.forEach(function (term) {
        html += '<li role="option"><a href="' + this.searchUrl + '?q=' + encodeURIComponent(term) + '" class="su-search-chip su-search-chip--trending" data-su-search-term="' + suEscapeHtml(term) + '">' +
                '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>' +
                '<span>' + suEscapeHtml(term) + '</span></a></li>';
      }.bind(this));
      html += '</ul></div>';
    }
    html += '</div>';
    this.predictiveSearchResults.innerHTML = html;
    this.setAttribute('results', 'true');
    this.open();
  }

  /* ── Live predictive results ─────────────────────────────────────── */
  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/ /g, "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    /* Request products + collections + queries + articles + pages, 6 of each */
    var params = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'product,collection,query,article,page',
      'resources[limit]': '6',
      'resources[limit_scope]': 'each',
      section_id: 'predictive-search'
    });

    /* 8s safety timeout */
    var ctrl = new AbortController();
    var timer = setTimeout(function () { ctrl.abort(); }, 8000);

    fetch(`${routes.predictive_search_url}?${params.toString()}`, { signal: ctrl.signal })
      .then((response) => {
        clearTimeout(timer);
        if (!response.ok) {
          this.close();
          throw new Error('search ' + response.status);
        }
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const sectionEl = doc.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = sectionEl ? sectionEl.innerHTML : '';
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((err) => {
        clearTimeout(timer);
        this.close();
        if (err && err.name !== 'AbortError') console.warn('predictive search error:', err);
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    setTimeout(() => { this.statusElement.setAttribute('aria-hidden', 'true'); }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    var liveCount = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveCount) this.setLiveRegionText(liveCount.textContent);
  }

  getResultsMaxHeight() {
    var header = document.getElementById('shopify-section-header');
    var headerBottom = header ? header.getBoundingClientRect().bottom : 0;
    this.resultsMaxHeight = window.innerHeight - headerBottom - 20;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
